import { format, addMinutes, parse, isValid } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

/**
 * Converts a time string to a Date object for a specific date.
 *
 * @param {string} time - The time string in format "h:mm a".
 * @param {Date|string|number} date - The date to apply the time to.
 * @param {string} timezone - The timezone for the time. Defaults to local.
 * @returns {Date} The Date object representing the converted time.
 */
export const timeToDate = (
  time,
  date = new Date(),
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  const dateStr =
    typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? date
      : format(new Date(date), "yyyy-MM-dd");
  let timeDate = parse(time, "h:mm a", new Date());
  if (!isValid(timeDate)) {
    timeDate = parse(time, "HH:mm", new Date());
  }
  const timeStr = format(timeDate, "HH:mm:ss");
  return zonedTimeToUtc(`${dateStr} ${timeStr}`, timezone);
};

/**
 * Converts a time string to a string taking a given timezone and date.
 *
 * @param {string} time - The time string in format "h:mm a".
 * @param {string} targetTimezone - The timezone to format the time to.
 * @param {Date|string|number} date - The date to consider for DST.
 * @param {string} sourceTimezone - The timezone the input time is in. Defaults to local.
 * @returns {string} A string representing the converted time.
 */
export const timeWithTimezoneToString = (
  time,
  targetTimezone,
  date = new Date(),
  sourceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  format = "h:mm a"
) => {
  if (!targetTimezone) {
    return time;
  }

  const dateObj = timeToDate(time, date, sourceTimezone);
  return formatInTimeZone(dateObj, targetTimezone, format);
};

export const getISODateTime = (date, time, sourceTimezone) => {
  const dateObj = timeToDate(time, date, sourceTimezone);
  return dateObj.toISOString();
};

export const getDateString = (date) => format(new Date(date), "yyyy-MM-dd");

/**
 * Adds the specified number of minutes to the given String time.
 *
 * @param {string} time - The time in the format "h:mm a".
 * @param {number} minutes - The number of minutes to add.
 * @param {string} targetTimezone - The timezone for the output time. Defaults to the computer's timezone.
 * @param {Date|string|number} date - The date to consider for DST.
 * @param {string} sourceTimezone - The timezone for the input time. Defaults to the computer's timezone.
 * @returns {string} The resulting time in the format "h:mm a".
 */
export const addMinutesToTime = (
  time,
  minutes,
  targetTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  date = new Date(),
  sourceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  const dateObj = timeToDate(time, date, sourceTimezone);
  const updatedDate = addMinutes(dateObj, minutes);
  return formatInTimeZone(updatedDate, targetTimezone, "h:mm a");
};
